import * as React from 'react';
import { AmorgAdminToken, Partner, partners, SiteView } from '../../../amorg-common';
import { Flex, FlexItem } from '../../../designSystem/compositeComponents/FlexHelpers';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { AmorgAdminPageProvider } from '../AmorgAdminPageContext';
import { SingleColumnLayout } from '../../../designSystem/layouts/SingleColumnLayout';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { colors } from '../../../designSystem/colors/color';
import { Icon } from '../../../designSystem/icons/Icons';
import { MessageCard } from '../../../designSystem/Message';
import { AdminSiteTabs } from '../_components/AdminSiteTabs';
import { FormField } from '../../../designSystem/formField/FormField';
import { TextInputWithSave } from '../../../designSystem/inputs/TextInputWithSave';
import { PageSection } from '../../../designSystem/PageSection';
import { CompanyInfo, fieldValidation, OrganizationContact, OrganizationView } from '../../../amorg-common';
import { createOrganizationContact, deleteOrganization, deleteOrganizationContact, getActiveUser, getAllSites, getOrganizationById, updateOrganization, updateOrganizationContact } from '../../../api/adminClientApi';
import { TextAreaWithSave } from '../../../designSystem/inputs/TextAreaWithSave';
import { ChipPanel } from '../../../designSystem/Chip';
import { Button } from '../../../designSystem/button/Button';
import { DeleteConfirmationButton } from '../../../designSystem/compositeComponents/DeleteConfirmationButton';
import { Card } from '../../../designSystem/Card';
import { OrganizationContactFlag, OrganizationContactFlags, OrganizationFlag, OrganizationFlags } from '../../../amorg-common/DTOs/Organization';
import { OrganizationContactModal } from './_components/OrganizationContactModal';
import { ActionChip, InfoChip } from '../../../designSystem/Chip';
import { SelectField } from '../../../designSystem/formField/SelectField';
import { SafeFlagSelector } from '../../../designSystem/compositeComponents/SafeFlagSelector';

type PageState = {
    status: 'starting'
} |
{
    status: 'ready',
    loading: boolean,
    organization: OrganizationView,
    orgSites: SiteView[]
}

export function AmorgAdminOrganizationInfo (): JSX.Element
{
    const [searchParams] = useSearchParams();

    const { organizationId } = useParams<{ organizationId: string; }>();
    const siteId = searchParams.get('siteId') || undefined;

    const [pageState, setPageState] = React.useState<PageState>({ status: 'starting' });
    const [activeUser, setActiveUser] = React.useState<AmorgAdminToken | null>(null);
    const [error, setError] = React.useState('');

    const navigate = useNavigate();

    React.useEffect(() =>
    {
        const init = async () => {

            if(!organizationId){
                alert('Mangler orgId i url');
                return;
            }

            const org = await getOrganizationById(organizationId, () => navigate('/amorgy/login'));
            if(!org){
                alert('Organisation ikke fundet: ' + organizationId);
                return;
            }

            const allSites = await getAllSites(() => navigate("/amorgy/login"));
            const orgSites = allSites.filter(s => s.organization?.id === organizationId);
            setActiveUser(await getActiveUser(() => navigate('/amorgy/login')));
            
            setPageState({
                status: 'ready',
                loading: false,
                organization: org,
                orgSites: orgSites
            });
        };
        init().catch(console.error);
    }, []);

    async function handleDeleteOrganization ()
    {
        if(pageState.status !== 'ready')
            return;

        const deleted = await deleteOrganization(pageState.organization.id, () => navigate('/amorgy/login'));
        if(!deleted)
        {
            setError('Organisationen kunne ikke slettes');
            return;
        }

        navigate('/amorgy/organizations');
    }

    async function handleChanged ()
    {
        setPageState(prev => prev.status === 'ready' ? { ...prev, loading: true } : prev);
        const org = await getOrganizationById(pageState.status === 'ready' ? pageState.organization.id : '', () => navigate("/amorgy/login"));
        if(!org)
            throw new Error('Organization not found');

        setPageState(prev => prev.status === 'ready' ? { ...prev, organization: org, loading: false } : prev);
    }



    return (
        <>
            
            <LoadingPageOverlay loading={pageState.status === 'starting' ? true : pageState.loading} />

            {pageState.status === 'ready' && 

                <AmorgAdminPageProvider>
                    <SingleColumnLayout
                        noDivider
                        headerContainer={
                            <Flex flexDirection="column" gap={16}>
                                <AdminSiteTabs selectedAdminTab='sites' selectedSiteTab='companyInfo'
                                    orgId={pageState.organization.id}
                                    siteId={siteId} />
                                {activeUser?.superAdmin &&
                                    <Flex justifyContent="flex-end">
                                        <DeleteConfirmationButton
                                            buttonText="Slet organisation"
                                            confirmationHeading="Slet organisation"
                                            confirmationText={`Skriv bekræftelseskoden for at slette organisationen "${pageState.organization.companyInfo.name}". Sites bliver ikke slettet, men mister organisationstilknytningen.`}
                                            requireKey
                                            onConfirmDelete={handleDeleteOrganization} />
                                    </Flex>
                                }
                            </Flex>
                        }

                        contentContainer={
                            <Flex flexDirection='column' gap={32}>
                                {error && <MessageCard variant="error">{error}</MessageCard>}
                                <>
                                    <MessageCard variant="success">
                                        <Flex gap={8}>
                                            <Icon name="status_ok" color={colors.icon.success} />
                                            <span>Ingen notifikationer</span>
                                        </Flex>
                                    </MessageCard>

                                    <CompanyInfoSection
                                        activeUser={activeUser}
                                        organization={pageState.organization}
                                        onChanged={handleChanged}
                                    />

                                    <SiteListSection sites={pageState.orgSites} selectedSiteId={siteId} />

                                    <ContactsSection
                                        organization={pageState.organization}
                                        onChanged={handleChanged}
                                    />
                                </>

                            </Flex>
                        } />
                </AmorgAdminPageProvider>

            }
        </>
    );
}

function CompanyInfoSection (
    {activeUser, organization, onChanged}: {
        activeUser: AmorgAdminToken | null,
        organization: OrganizationView,
        onChanged: () => void
    }): JSX.Element
{
    const [error, setError] = React.useState<string>('');

    React.useEffect(() =>
    {
        setError('');
    }, [organization]);

    const navigate = useNavigate();

    async function handleChange (value:CompanyInfo)
    {
        if(!fieldValidation.organization.companyInfo(value))
            setError('Fejl i en indtastning');
        else
        {
            const result = await updateOrganization(organization.id, value, organization.partner, () => navigate('/amorgy/login'));
            if(result)
            {
                setError('');
                onChanged();
            }
            else
            {
                setError('Fejl ved opdatering af stamdata');
            }
        }
    }

    return (
        <PageSection
            header={{
                title:'Oplysninger om organisation',
                defaultCollapsed: false,
                icon: 'facility_domain'
            }}
        >

            <Flex flexDirection="column" gap={16}>

                {error && <MessageCard variant="error">{error}</MessageCard>}
                

                <FormField
                    label='Navn'
                    inputComponent={
                        <TextInputWithSave
                            type={'text'}
                            value={organization.companyInfo.name}
                            onSave={(value) => handleChange({...organization.companyInfo, name: value})}
                        />
                    }
                />
                
                {activeUser?.superAdmin &&
                    <SelectField
                        label='Partner'
                        options={partners.map(p => ({ text:p, value:p }))}
                        emptyText='Ingen partner valgt'
                        selectedValue={organization.partner || ''}
                        onChange={async (value) =>
                        {
                            const result = await updateOrganization(organization.id, organization.companyInfo, value ? value as Partner : null, () => navigate('/amorgy/login'));
                            if(result)
                            {
                                onChanged();
                            }
                        }}
                    />
                }

                <SafeFlagSelector 
                    allowedFlags={OrganizationFlags.map(f => f)} 
                    selectedFlags={organization.companyInfo.flags} 
                    colorMode='colorIndex'
                    onChange={async (newFlags) => {
                        handleChange({...organization.companyInfo, flags:newFlags as OrganizationFlag[]});
                    }}
                />

                <Flex flexDirection="row" gap={16}>
                    <FlexItem flexGrow="1">
                        <FormField
                            label='Adresse'
                            inputComponent={
                                <TextInputWithSave
                                    type={'text'}
                                    value={organization.companyInfo.address}
                                    onSave={(value) => handleChange({...organization.companyInfo, address: value})}
                                />
                            }
                        />
                    </FlexItem>
                    <FlexItem flexGrow="1">
                        <FormField
                            label='C/O adresse'
                            inputComponent={
                                <TextInputWithSave
                                    type={'text'}
                                    value={organization.companyInfo.altAddress}
                                    onSave={(value) => handleChange({...organization.companyInfo, altAddress: value})}
                                />
                            }
                        />
                    </FlexItem>
                </Flex>
                <Flex flexDirection="row" gap={16}>
                    <FlexItem flexGrow="1">
                        <FormField
                            label='Postnummer'
                            inputComponent={
                                <TextInputWithSave
                                    type={'text'}
                                    value={organization.companyInfo.zip}
                                    onSave={(value) => handleChange({...organization.companyInfo, zip: value})}
                                />
                            }
                        />
                    </FlexItem>
                    <FlexItem flexGrow="1">
                        <FormField
                            label='By'
                            inputComponent={
                                <TextInputWithSave
                                    type={'text'}
                                    value={organization.companyInfo.city}
                                    onSave={(value) => handleChange({...organization.companyInfo, city: value})}
                                />
                            }
                        />
                    </FlexItem>
                </Flex>

                <Flex flexDirection="row" gap={16}>
                    <FlexItem flexGrow="1">
                        <FormField
                            label='Telefon'
                            inputComponent={
                                <TextInputWithSave
                                    type={'text'}
                                    value={organization.companyInfo.phone}
                                    onSave={(value) => handleChange({...organization.companyInfo, phone: value})}
                                />
                            }
                        />
                    </FlexItem>
                    <FlexItem flexGrow="1">
                        <FormField
                            label='Website'
                            inputComponent={
                                <TextInputWithSave
                                    type={'text'}
                                    value={organization.companyInfo.website}
                                    onSave={(value) => handleChange({...organization.companyInfo, website: value})}
                                />
                            }
                        />
                    </FlexItem>
                </Flex>

                <FormField
                    label='Note'
                    inputComponent={
                        <TextAreaWithSave
                            minRows={3}
                            value={organization.companyInfo.note}
                            onSave={(value) => handleChange({...organization.companyInfo, note: value})}
                        />
                    }
                />

            </Flex>
        </PageSection>
    );
}

function SiteListSection (
    {sites, selectedSiteId}: {
        sites: SiteView[],
        selectedSiteId: string | undefined
    }): JSX.Element
{
    const [redirectToSiteId, setRedirectToSiteId] = React.useState('');

    return (
        <>
            {redirectToSiteId && <Navigate to={`/amorgy/sites/${redirectToSiteId}`} replace={true} />}
        
            <PageSection
                styleVariant="default-no-card"
                header={{
                    title:'Sites',
                    defaultCollapsed: false,
                    icon: 'captive_portal'
                }}
            >
                {sites.length === 0 &&
                    <i>Ingen sites tilknyttet organisationen</i>
                }

                <ChipPanel>
                    {sites.map(site => {
                        
                        return  <ActionChip key={site.id} 
                                    variant={site.id === selectedSiteId ? 'filled' : 'outlined'}
                                    trailingIcon="interface_actions_externalLink"
                                    onClick={() => setRedirectToSiteId(site.id)}>
                                    {site.loginName}
                                </ActionChip>
                    })}
                </ChipPanel>
            </PageSection>
        </>
    );
}

function ContactsSection (
    { organization, onChanged }: {
        organization: OrganizationView,
        onChanged: () => void
    }): JSX.Element
{
    const navigate = useNavigate();
    const [error, setError] = React.useState('');

    const [createModalOpen, setCreateModalOpen] = React.useState(false);
    const [editingContact, setEditingContact] = React.useState<OrganizationContact | null>(null);

    const contactModalOpen = createModalOpen || editingContact !== null;

    function openCreateModal ()
    {
        setError('');
        setCreateModalOpen(true);
    }

    function openEditModal (contact: OrganizationContact)
    {
        setError('');
        setEditingContact(contact);
    }

    function closeContactModal ()
    {
        setCreateModalOpen(false);
        setEditingContact(null);
    }

    async function handleCreateContact (payload: Omit<OrganizationContact, 'id'>): Promise<boolean>
    {
        if (!fieldValidation.organization.contact.object({ ...payload, id: 'temp' } as OrganizationContact))
        {
            setError('Fejl i kontaktoplysninger');
            return false;
        }

        const result = await createOrganizationContact(organization.id, payload, () => navigate('/amorgy/login'));
        if (!result)
        {
            setError('Fejl ved oprettelse af kontakt');
            return false;
        }

        setError('');
        await onChanged();
        return true;
    }

    async function handleUpdateContact (contact: OrganizationContact): Promise<boolean>
    {
        if (!fieldValidation.organization.contact.object(contact))
        {
            setError('Fejl i kontaktoplysninger');
            return false;
        }

        const result = await updateOrganizationContact(organization.id, contact, () => navigate('/amorgy/login'));
        if (!result)
        {
            setError('Fejl ved opdatering af kontakt');
            return false;
        }

        setError('');
        await onChanged();
        return true;
    }

    async function handleSaveContactModal (contact: Omit<OrganizationContact, 'id'>): Promise<boolean>
    {
        if (editingContact)
        {
            const updatedContact: OrganizationContact = {
                ...editingContact,
                ...contact
            };

            return handleUpdateContact(updatedContact);
        }

        return handleCreateContact(contact);
    }

    async function handleDeleteContact (contactId: string)
    {
        const result = await deleteOrganizationContact(organization.id, contactId, () => navigate('/amorgy/login'));
        if (!result)
        {
            setError('Fejl ved sletning af kontakt');
            return;
        }

        setError('');
        await onChanged();
    }

    return (
        <PageSection styleVariant="default-no-card"
            header={{
                title: 'Kontakter',
                defaultCollapsed: false,
                icon: 'interface_user_team',
                button: {
                    variant: "filled",
                    icon: "interface_user_addUser",
                    text: "Opret kontakt",
                    onClick: openCreateModal
                }
            }}
        >
            <Flex flexDirection="column" gap={16}>
                
                {error && !contactModalOpen && <MessageCard variant="error">{error}</MessageCard>}

                {organization.contacts.length === 0 &&
                    <i>Ingen kontakter oprettet</i>
                }

                <Flex flexDirection="column" gap={12}>
                    {organization.contacts.map(contact =>
                        <Card key={contact.id} variant="outlined" padding="12px">

                            <Flex>

                                <Flex flexDirection="column" gap={8} grow="1">
                                    <Flex gap={8} alignItems="center">
                                        <Icon name="interface_user_user" />
                                        <span>{contact.name}</span>
                                    </Flex>
                                    <ChipPanel>
                                        {OrganizationContactFlags.map((flag,i) => 
                                            <InfoChip key={i} variant={contact.flags.includes(flag as OrganizationContactFlag) ? 'filled' : 'outlined'}>
                                                {flag}
                                            </InfoChip>
                                        )}
                                    </ChipPanel>
                                </Flex>


                                <Flex flexDirection="column" grow="1">

                                    <Flex gap={8} alignItems="center">
                                        <Icon name="interface_glyph_phone" />
                                        <span>{contact.phone}</span>
                                    </Flex>

                                    
                                    <Flex gap={8} alignItems="center">
                                        <Icon name="interface_glyph_email" />
                                        <span>{contact.email}</span>
                                    </Flex>

                                </Flex>

                                <Flex flexDirection="column" grow="2">

                                    <FormField
                                        label="Note"
                                        inputComponent={
                                            <span>{contact.note}</span>
                                        }
                                    />
                                </Flex>

                            </Flex>
                            
                            <Flex gap={8} justifyContent="flex-end">

                                    <Flex gap={8}>
                                        <Button
                                            variant="text"
                                            icon="interface_actions_edit"
                                            text="Rediger"
                                            onClick={() => openEditModal(contact)}
                                        />
                                    </Flex>

                                    <DeleteConfirmationButton
                                        variant="destructive-text"
                                        buttonText="Slet"
                                        confirmationHeading="Slet kontakt"
                                        confirmationText="Er du sikker på, at du vil slette kontakten?"
                                        requireKey={false}
                                        onConfirmDelete={() => handleDeleteContact(contact.id)}
                                    />
                            </Flex>
                        </Card>
                    )}
                </Flex>

                <OrganizationContactModal
                    contact={editingContact}
                    showModal={contactModalOpen}
                    error={error}
                    onCancel={closeContactModal}
                    onSave={handleSaveContactModal}
                />
            </Flex>
        </PageSection>
    );
}